import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";

const LoginPage = () => {
  const { login, user } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user && user !== false) nav("/espace-client");
  }, [user, nav]);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    const res = await login(email.trim(), password);
    setLoading(false);
    if (res.ok) {
      toast.success("Bienvenue dans votre espace client");
      nav("/espace-client");
    } else {
      setError(res.error);
    }
  };

  return (
    <div data-testid="login-page" className="min-h-screen bg-white grid lg:grid-cols-2">
      <div className="hidden lg:block relative bg-teal-dark grain overflow-hidden">
        <div className="absolute inset-0 p-12 flex flex-col justify-between text-white">
          <Link to="/" className="inline-flex items-center gap-2 text-teal-l3 hover:text-white text-sm" data-testid="login-back-home">
            <ArrowLeft size={16} /> Retour au site
          </Link>
          <div>
            <p className="eyebrow text-teal-l3">Espace client</p>
            <h1 className="mt-4 font-serif text-5xl leading-[1.02] font-light">
              Votre tableau de bord,<br /><em className="text-teal-l3">accessible en un clic.</em>
            </h1>
            <p className="mt-6 text-teal-l3 max-w-md">
              Suivez vos indicateurs clés en temps réel, consultez vos documents et échangez
              avec votre expert-comptable dédié.
            </p>
          </div>
          <div className="text-xs text-teal-l3">© 2026 Ducrot &amp; Associés</div>
        </div>
      </div>

      <div className="flex items-center justify-center p-8">
        <form onSubmit={submit} data-testid="login-form" className="w-full max-w-md">
          <Link to="/" className="lg:hidden inline-flex items-center gap-2 text-teal-dark text-sm mb-6">
            <ArrowLeft size={16} /> Retour
          </Link>
          <p className="eyebrow text-teal-accent">Connexion</p>
          <h2 className="mt-3 font-serif text-4xl text-teal-dark">Accédez à votre espace</h2>
          <p className="mt-3 text-ink-500 text-sm">
            Utilisez les identifiants fournis par votre expert-comptable.
          </p>

          <div className="mt-8 space-y-5">
            <label className="block">
              <span className="eyebrow text-ink-500 block mb-2">Adresse email</span>
              <input
                data-testid="login-email"
                type="email" required autoFocus
                value={email} onChange={(e) => setEmail(e.target.value)}
                className="w-full border border-ink-200 px-4 py-3 focus:border-teal-accent focus:outline-none focus:ring-2 focus:ring-teal-accent/20"
              />
            </label>
            <label className="block">
              <span className="eyebrow text-ink-500 block mb-2">Mot de passe</span>
              <input
                data-testid="login-password"
                type="password" required
                value={password} onChange={(e) => setPassword(e.target.value)}
                className="w-full border border-ink-200 px-4 py-3 focus:border-teal-accent focus:outline-none focus:ring-2 focus:ring-teal-accent/20"
              />
            </label>
          </div>

          {error && (
            <div data-testid="login-error" className="mt-4 border-l-4 border-red-500 bg-red-50 text-red-800 text-sm px-4 py-3">
              {error}
            </div>
          )}

          <button
            type="submit"
            data-testid="login-submit"
            disabled={loading}
            className="btn-primary mt-8 w-full justify-center disabled:opacity-60"
          >
            {loading ? "Connexion…" : (<>Se connecter <ArrowUpRight size={16} /></>)}
          </button>

          <p className="mt-8 text-xs text-ink-500">
            Un souci de connexion ?{" "}
            <Link to="/contact" className="underline hover:text-teal-accent">Contactez-nous</Link>.
          </p>

          <div className="mt-6 pt-6 border-t border-ink-200">
            <p className="text-sm text-ink-500">
              Pas encore de compte ?{" "}
              <Link to="/inscription" data-testid="login-register-link" className="text-teal-dark underline hover:text-teal-accent">
                Créer un compte
              </Link>
            </p>
          </div>
        </form>
      </div>
    </div>
  );
};

export default LoginPage;
